import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Gift, Users, TrendingUp, RotateCcw, Loader2, Search, Trophy, AlertCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReferrals() {
  const [data, setData] = useState({ summary: null, rows: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "all", referrer_email: "", date_from: "", date_to: "" });
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  const [reversing, setReversing] = useState(false);
  const [programEnabled, setProgramEnabled] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params.set(k, v); });
      const [list, prog] = await Promise.all([
        fetch(`${API}/admin/referrals?${params.toString()}`, { credentials: "include" }).then((r) => r.ok ? r.json() : { summary: null, rows: [] }),
        fetch(`${API}/admin/settings/referral`, { credentials: "include" }).then((r) => r.ok ? r.json() : null),
      ]);
      setData(list);
      setProgramEnabled(!!(prog && prog.enabled));
    } catch { toast.error("Failed to load referrals"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doReverse = async () => {
    if (!reverseTarget) return;
    setReversing(true);
    try {
      const r = await fetch(`${API}/admin/referrals/${reverseTarget.referral_id}/reverse`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reverseReason || "manual_admin_reverse" }),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || "Reverse failed");
      toast.success("Referral reversed and credits clawed back");
      setReverseTarget(null); setReverseReason("");
      load();
    } catch (e) { toast.error(e.message); }
    finally { setReversing(false); }
  };

  const summary = data.summary || { total: 0, credited: 0, pending: 0, reversed: 0, total_credits_issued: 0, top_referrers: [] };

  return (
    <AdminLayout activeNav="referrals">
      <div className="p-4 sm:p-6 space-y-5" data-testid="admin-referrals">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Gift size={18} />
          </div>
          <div className="flex-1">
            <h1 className="text-lg sm:text-xl font-extrabold text-gray-900">Referral Program</h1>
            <p className="text-xs text-gray-500">Track referrals, credits issued, and reverse bonuses if needed.</p>
          </div>
        </div>

        {!programEnabled && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-start gap-2 text-xs text-amber-900" data-testid="referrals-paused-banner">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              Program is currently <span className="font-bold">paused</span>. Existing pending referrals are frozen and will resume crediting when you re-enable from <a href="/admin/settings" className="underline">Settings</a>.
            </span>
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryCard label="Total" value={summary.total} icon={<Users size={14} />} color="bg-gray-50 text-gray-900 border-gray-100" testid="referrals-summary-total" />
          <SummaryCard label="Credited" value={summary.credited} color="bg-emerald-50 text-emerald-700 border-emerald-100" testid="referrals-summary-credited" />
          <SummaryCard label="Pending" value={summary.pending} color="bg-amber-50 text-amber-700 border-amber-100" testid="referrals-summary-pending" />
          <SummaryCard label="Reversed" value={summary.reversed} color="bg-red-50 text-red-700 border-red-100" testid="referrals-summary-reversed" />
          <SummaryCard label="Credits Issued" value={`₹${summary.total_credits_issued}`} icon={<TrendingUp size={14} />} color="bg-blue-50 text-blue-700 border-blue-100" testid="referrals-summary-issued" />
        </div>

        {/* Top referrers */}
        {summary.top_referrers && summary.top_referrers.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-4" data-testid="referrals-top-referrers">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={14} className="text-amber-500" />
              <h2 className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Top Referrers</h2>
            </div>
            <ol className="divide-y divide-gray-50">
              {summary.top_referrers.map((r, i) => (
                <li key={r.user_id} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-900 truncate">{r.email || r.name || "—"}</p>
                      {r.name && <p className="text-[10px] text-gray-400 truncate">{r.name}</p>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold text-emerald-600">₹{r.earned}</p>
                    <p className="text-[10px] text-gray-400">{r.count} referral{r.count !== 1 ? "s" : ""}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 sm:items-end">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 block">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
                <SelectTrigger className="text-xs" data-testid="referrals-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="credited">Credited</SelectItem>
                  <SelectItem value="reversed">Reversed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 block">Referrer email</Label>
              <Input value={filters.referrer_email} onChange={(e) => setFilters((p) => ({ ...p, referrer_email: e.target.value }))} placeholder="contains…" className="text-xs" data-testid="referrals-filter-email" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 block">From</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))} className="text-xs" data-testid="referrals-filter-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 block">To</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))} className="text-xs" data-testid="referrals-filter-to" />
            </div>
            <Button onClick={load} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="referrals-search">
              {loading ? <Loader2 size={13} className="animate-spin" /> : <><Search size={13} className="mr-1" /> Search</>}
            </Button>
          </div>
        </div>

        {/* Rows */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {loading && data.rows.length === 0 ? (
            <div className="p-8 text-center text-gray-400"><Loader2 size={16} className="animate-spin inline" /></div>
          ) : data.rows.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No referrals match your filters yet.</div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-100 bg-gray-50">
                      <th className="px-3 py-2 text-left font-medium">Code</th>
                      <th className="px-3 py-2 text-left font-medium">Referrer</th>
                      <th className="px-3 py-2 text-left font-medium">Referee</th>
                      <th className="px-3 py-2 text-center font-medium">Status</th>
                      <th className="px-3 py-2 text-right font-medium">Credit Each</th>
                      <th className="px-3 py-2 text-left font-medium">Created</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.referral_id} className="border-b border-gray-50 last:border-0" data-testid={`referral-row-${r.referral_id}`}>
                        <td className="px-3 py-2 font-mono text-emerald-700 font-semibold">{r.code_used}</td>
                        <td className="px-3 py-2"><div className="text-gray-900">{r.referrer_name || "—"}</div><div className="text-[10px] text-gray-400">{r.referrer_email}</div></td>
                        <td className="px-3 py-2"><div className="text-gray-900">{r.referee_name || "—"}</div><div className="text-[10px] text-gray-400">{r.referee_email}</div></td>
                        <td className="px-3 py-2 text-center"><StatusPill status={r.status} /></td>
                        <td className="px-3 py-2 text-right text-gray-700">₹{r.referrer_credit} / ₹{r.referee_credit}</td>
                        <td className="px-3 py-2 text-gray-500">{(r.created_at || "").slice(0, 10)}</td>
                        <td className="px-3 py-2 text-right">
                          {r.status === "credited" && (
                            <Button size="sm" variant="outline" className="text-[10px] h-7 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setReverseTarget(r)} data-testid={`referral-reverse-${r.referral_id}`}>
                              <RotateCcw size={11} className="mr-1" /> Reverse
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-50">
                {data.rows.map((r) => (
                  <div key={r.referral_id} className="p-3" data-testid={`referral-card-${r.referral_id}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-mono text-xs font-bold text-emerald-700">{r.code_used}</span>
                      <StatusPill status={r.status} />
                    </div>
                    <p className="text-xs text-gray-700 truncate">{r.referrer_email} → {r.referee_email}</p>
                    <p className="text-[10px] text-gray-400 mt-1">₹{r.referrer_credit} / ₹{r.referee_credit} · {(r.created_at || "").slice(0, 10)}</p>
                    {r.status === "credited" && (
                      <Button size="sm" variant="outline" className="text-[10px] h-7 text-red-600 border-red-200 mt-2" onClick={() => setReverseTarget(r)}>
                        <RotateCcw size={11} className="mr-1" /> Reverse
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={!!reverseTarget} onOpenChange={(v) => { if (!v) { setReverseTarget(null); setReverseReason(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reverse referral?</DialogTitle>
          </DialogHeader>
          {reverseTarget && (
            <div className="text-sm text-gray-600 space-y-3">
              <p>This will reverse <span className="font-semibold">₹{reverseTarget.referrer_credit}</span> from <span className="font-semibold">{reverseTarget.referrer_email}</span> and <span className="font-semibold">₹{reverseTarget.referee_credit}</span> from <span className="font-semibold">{reverseTarget.referee_email}</span>.</p>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 block">Reason (optional)</Label>
                <Input value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} placeholder="e.g. fraud_suspected" className="text-xs" data-testid="referral-reverse-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReverseTarget(null); setReverseReason(""); }}>Cancel</Button>
            <Button onClick={doReverse} disabled={reversing} className="bg-red-600 hover:bg-red-700 text-white" data-testid="referral-reverse-confirm">
              {reversing ? <Loader2 size={13} className="animate-spin mr-1" /> : <RotateCcw size={13} className="mr-1" />}
              Reverse credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function SummaryCard({ label, value, icon, color, testid }) {
  return (
    <div className={`rounded-2xl border p-4 ${color}`} data-testid={testid}>
      <div className="flex items-center gap-1.5">
        {icon && <div className="opacity-70">{icon}</div>}
        <span className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</span>
      </div>
      <p className="text-2xl font-extrabold mt-1.5">{value}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    credited: "bg-emerald-100 text-emerald-700",
    pending: "bg-amber-100 text-amber-700",
    reversed: "bg-red-100 text-red-700",
    blocked: "bg-gray-100 text-gray-500",
  };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${map[status] || "bg-gray-100 text-gray-500"}`}>{status}</span>;
}
